import { createWorker } from 'tesseract.js';

/**
 * Amount extraction: tries keyword-anchored patterns first (TOTAL, MONTANT, BETRAG, SUMME),
 * then falls back to the largest price-looking number in the text.
 */
const TOTAL_KEYWORD_RE =
  /(?:total|montant|betrag|summe|amount|gesamt|zu\s*zahlen|à\s*payer)[^\d]*(\d{1,5}[.,]\d{2})/gi;

const PRICE_RE = /(\d{1,5}[.,]\d{2})/g;

function normalizeAmount(raw) {
  // Handle "1.234,56" (European thousands+decimal) vs "1,234.56" (US) vs "12,34"
  const commaDecimal = /^\d{1,3}(\.\d{3})*,\d{2}$/.test(raw);
  if (commaDecimal) return parseFloat(raw.replace(/\./g, '').replace(',', '.'));
  return parseFloat(raw.replace(',', '.'));
}

function parseAmount(text) {
  // Priority 1: total-keyword match
  const keywordMatches = [...text.matchAll(TOTAL_KEYWORD_RE)];
  if (keywordMatches.length) {
    const value = normalizeAmount(keywordMatches[keywordMatches.length - 1][1]);
    if (!isNaN(value) && value > 0 && value < 100000) return value.toFixed(2);
  }

  // Priority 2: largest price-looking number (receipts put total last / largest)
  const allPrices = [...text.matchAll(PRICE_RE)]
    .map((m) => normalizeAmount(m[1]))
    .filter((v) => !isNaN(v) && v > 0 && v < 100000);

  if (allPrices.length) return Math.max(...allPrices).toFixed(2);
  return null;
}

/**
 * Date extraction: handles the most common European and ISO formats.
 */
const DATE_PATTERNS = [
  // DD/MM/YYYY  DD.MM.YYYY  DD-MM-YYYY
  {
    re: /\b(\d{2})[./-](\d{2})[./-](\d{4})\b/,
    build: (m) => `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`,
  },
  // YYYY-MM-DD
  {
    re: /\b(\d{4})-(\d{2})-(\d{2})\b/,
    build: (m) => `${m[1]}-${m[2]}-${m[3]}`,
  },
  // DD MonthName YYYY  (e.g. "15 Jan 2024", "15 January 2024")
  {
    re: /\b(\d{1,2})\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+(\d{4})\b/i,
    build: (m) => {
      const monthIndex = {
        jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
        jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
      };
      const month = String(monthIndex[m[2].slice(0, 3).toLowerCase()]).padStart(2, '0');
      return `${m[3]}-${month}-${String(m[1]).padStart(2, '0')}`;
    },
  },
];

function parseDate(text) {
  const now = new Date();
  for (const { re, build } of DATE_PATTERNS) {
    const match = text.match(re);
    if (!match) continue;
    const iso = build(match);
    const d = new Date(iso);
    if (!isNaN(d.getTime()) && d.getFullYear() >= 2000 && d <= now) return iso;
  }
  return null;
}

/**
 * Merchant extraction: the first meaningful non-numeric line near the top of the receipt.
 */
const MERCHANT_SKIP_RE =
  /^[\d\s\W]+$|receipt|invoice|bon\s|quittung|kassenbon|facture|rechnung|tva|mwst|ust\.?-?id/i;

function parseMerchant(text) {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  for (const line of lines.slice(0, 8)) {
    if (line.length < 3) continue;
    if (/^\d/.test(line)) continue;           // starts with digit
    if (MERCHANT_SKIP_RE.test(line)) continue;
    // Keep only printable chars, strip stray OCR artifacts
    const cleaned = line.replace(/[^a-zA-ZÀ-ÿ0-9\s&'.,-]/g, '').trim();
    if (cleaned.length >= 3) return cleaned;
  }
  return null;
}

/**
 * Run Tesseract OCR on an image File/Blob and return parsed receipt fields.
 * PDF files are not supported and will return all-null suggestions.
 *
 * @param {File} imageFile
 * @param {function} [onProgress]  called with 0–100 progress values
 * @returns {Promise<{ amount: string|null, date: string|null, merchant: string|null }>}
 */
// Self-hosted OCR assets (see copy-ocr-assets.js): same-origin so the service
// worker caches them and OCR also works offline
const LOCAL_OCR_PATHS = {
  workerPath: '/tesseract/worker.min.js',
  corePath: '/tesseract/',
  langPath: '/tesseract/lang',
};

let localAssetsAvailable = null;
async function ocrPaths() {
  if (localAssetsAvailable === null) {
    try {
      // GET, not HEAD: the service worker only answers GETs from its cache,
      // so this also succeeds offline once the engine is cached
      const res = await fetch(LOCAL_OCR_PATHS.workerPath);
      // A missing file falls through to index.html, so check the type too
      localAssetsAvailable = res.ok && !(res.headers.get('content-type') || '').includes('text/html');
    } catch {
      localAssetsAvailable = false;
    }
  }
  // Without local files (e.g. dev without the copy step) use the CDN defaults
  return localAssetsAvailable ? LOCAL_OCR_PATHS : {};
}

/**
 * Load the OCR engine once while online so the service worker caches it;
 * afterwards receipts can be read offline. No-op once cached.
 */
export async function warmOcrCache() {
  try {
    if (!navigator.onLine || !('caches' in window)) return;
    if (await caches.match(`${LOCAL_OCR_PATHS.langPath}/eng.traineddata.gz`)) return;
    const paths = await ocrPaths();
    if (!paths.workerPath) return;
    const worker = await createWorker('eng', 1, paths);
    await worker.terminate();
  } catch (err) {
    console.log('OCR warm-up skipped:', err);
  }
}

export async function runOcrOnImage(imageFile, onProgress) {
  const worker = await createWorker('eng', 1, {
    ...(await ocrPaths()),
    logger: onProgress
      ? (m) => {
          if (m.status === 'recognizing text') onProgress(Math.round(m.progress * 100));
        }
      : undefined,
  });

  try {
    const { data: { text } } = await worker.recognize(imageFile);
    return {
      amount: parseAmount(text),
      date: parseDate(text),
      merchant: parseMerchant(text),
    };
  } finally {
    await worker.terminate();
  }
}
